import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useMediaQuery } from '@material-ui/core'

import './dropDownHL.css'
import TooltipHLPure from '../tooltipHL/tooltipHL.pure'
import LabelErrorPure from '../labelErrorPure/labelErrorPure'
import { FilterIcon } from '../../../assets/logos/icons'
import ReactDOM from 'react-dom'

/**
 * @author Marissa Vargas
 * @date 2025-04-22
 * @description This is a customized component to show in desktop devices only. Its main objective is to limit the amount of options displayed when the list of options is too big. It imitates HTML native behavior, such as keyboard features and screen reader support.
 * @param {Object} props The properties passed to the component.
 * @param {string} [props.labelDropDown] The label for the dropdown.
 * @param {Array} props.optionsDropDown The options for the dropdown.
 * @param {function} props.onDropDownChange The function to call when the dropdown value changes.
 * @param {(string|number)} props.optionsValue The value of the selected option.
 * @param {string} props.valueItem The key to use for the value in the options array.
 * @param {string} props.nameOption The key to use for the display name in the options array.
 * @param {boolean} [props.flex] Whether to use flexbox for layout.
 * @param {boolean} [props.tooltipShow] Whether to show the tooltip.
 * @param {'top'|'left'|'right'|'bottom'} [props.positionTooltip] The position of the tooltip.
 * @param {string} [props.titleTooltip] The title of the tooltip.
 * @param {string} [props.descriptionTooltip] The description of the tooltip.
 * @param {string} [props.subContent] Additional content for the tooltip.
 * @param {string} [props.restContent] Additional content for the tooltip.
 * @param {boolean} [props.informationIcon] Whether to show the information icon.
 * @param {boolean} [props.disable] Whether the dropdown is disabled.
 * @param {boolean} [props.required] Whether the dropdown is required.
 * @param {'bottom'|'top'} [props.position] The position of the option list, default is 'bottom'.
 * @param {boolean} [props.iconFilter] Whether to show the filter icon or not, default is false.
 * @param {boolean} [props.keepPlaceholderTop=true] When a placeholder like "Sin asignar" exists, keep it as the first option and do not sort it.
 * @param {boolean} [props.hidePlaceholder=false] When true, removes placeholder options (e.g., "Sin asignar") from the list entirely.
 * @param {string}  [props.sortLocale] Optional BCP-47 locale (e.g., 'es', 'en'); defaults to the browser locale.
 * @returns {JSX.Element} The rendered component.
 * @example
 * ```
 *  * jsx
 * <DropDownBtnHLPure
 *  labelDropDown={t('labels.fondo')}
 *  optionsDropDown={[ name: 'Fondo 1', value: 'fondo1' }, { name: 'Fondo 2', value: 'fondo2' }]}
 *  optionsValue={fund}
 *  valueItem={'value'}
 *  nameOption={'name'}
 *  onDropDownChange={(e) =>
 *    setFund(e.target.value)
 *  }
 *  position='top'
 *  iconFilter={true}
 *  required
 * />
 * ```
 */
const isPlaceholderOption = (option, valueItem, nameOption) => {
  const raw = typeof option === 'object' && option !== null
    ? (nameOption && option[nameOption] != null ? option[nameOption] : option[valueItem])
    : option
  const label = (raw ?? '').toString().trim().toLowerCase()
  if (!label) return true
  const placeholders = [
    'sin asignar',
    'seleccione',
    'seleccionar',
    'selecciona',
    'seleccione una opción',
    'seleccionar...',
    'seleccione...',
    'select',
    'select one',
    'select an option',
    'select...',
    'unassigned',
    'none',
    'n/a',
    'no aplica',
    '-',
    'todas',
    'todos',
    'todas las empresas',
    'todos los',
    'todas las',
    'todo',
    'toda',
    'all',
    'all companies',
    'all items',
    'ver todos',
    'ver todas',
    'mostrar todos',
    'mostrar todas',
    'show all',
    'view all',
    'selecciona una empresa',
    "select a company"
  ]
  return placeholders.includes(label)
}

const getDisplayLabel = (option, valueItem, nameOption) => {
  if (typeof option === 'object' && option !== null) {
    const candidate = nameOption && option[nameOption] != null
      ? option[nameOption]
      : (valueItem ? option[valueItem] : option)
    return candidate != null ? String(candidate) : ''
  }
  return option != null ? String(option) : ''
}

const useSortedOptions = (optionsDropDown = [], valueItem, nameOption, keepPlaceholderTop = true, hidePlaceholder = false, sortLocale) => {
  return useMemo(() => {
    const items = Array.isArray(optionsDropDown) ? [...optionsDropDown] : []

    const collator = new Intl.Collator([
      sortLocale || (typeof navigator !== 'undefined' ? navigator.language : undefined) || 'es',
      'es',
      'en'
    ].filter(Boolean), {
      usage: 'sort',
      sensitivity: 'accent',
      numeric: true,
      ignorePunctuation: true
    })

    const placeholders = items.filter((opt) => isPlaceholderOption(opt, valueItem, nameOption))
    const rest = items.filter((opt) => !isPlaceholderOption(opt, valueItem, nameOption))

    rest.sort((a, b) => collator.compare(
      getDisplayLabel(a, valueItem, nameOption),
      getDisplayLabel(b, valueItem, nameOption)
    ))

    if (hidePlaceholder) return rest
    if (keepPlaceholderTop && placeholders.length) return [placeholders[0], ...rest]
    const together = [...rest, ...placeholders]
    together.sort((a, b) => collator.compare(
      getDisplayLabel(a, valueItem, nameOption),
      getDisplayLabel(b, valueItem, nameOption)
    ))
    return together
  }, [optionsDropDown, valueItem, nameOption, keepPlaceholderTop, hidePlaceholder, sortLocale])
}

const DropDownBtnHLPure = (props) => {
  const {
    labelDropDown,
    optionsDropDown,
    onDropDownChange,
    optionsValue,
    valueItem,
    nameOption,
    flex,
    tooltipShow,
    positionTooltip,
    titleTooltip,
    descriptionTooltip,
    subContent,
    restContent,
    informationIcon,
    disable,
    required,
    position = 'bottom',
    iconFilter = false,
    open = false,
    alwaysOpen = false,
    showError,
    errorText,
    id = '',
    keepPlaceholderTop = true,
    hidePlaceholder = false,
    sortLocale,
    subLabel = "",
    tooltipShowSubLabel,
    positionTooltipSubLabel,
    titleTooltipSubLabel,
    descriptionTooltipSubLabel,
    subContentSubLabel,
    restContentSubLabel,
    informationIconSubLabel,
  } = props

  const [isOpen, setIsOpen] = useState(open)
  const [selectedValue, setSelectedValue] = useState(optionsValue)
  const [currentOptionIndex, setCurrentOptionIndex] = useState(0)
  const dropdownRef = useRef(null)
  const buttonRef = useRef(null)
  const optionRefs = useRef([])
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 })
  const [menuZIndex, setMenuZIndex] = useState(10000)
  const [menuPositioned, setMenuPositioned] = useState(false)
  const [menuDirection, setMenuDirection] = useState('down') // 'down' | 'up'
  const [menuMaxHeight, setMenuMaxHeight] = useState(220)

  const sortedOptions = useSortedOptions(
    optionsDropDown,
    valueItem,
    nameOption,
    keepPlaceholderTop,
    hidePlaceholder,
    sortLocale
  )

  const updatePosition = () => {
    const rect = dropdownRef.current?.getBoundingClientRect()
    if (!rect) return
    const GAP = 4
    const viewportH = window.innerHeight || document.documentElement.clientHeight
    const aboveSpace = rect.top // px available above (viewport coords)
    const belowSpace = viewportH - rect.bottom // px available below

    // Measure current menu height if available; otherwise, assume desired max
    const desiredMax = 220
    const measuredH = menuRef.current?.offsetHeight || desiredMax

    // Choose direction: prefer down; flip up if not enough space below and there is more space above
    let direction = 'down'
    if (belowSpace < Math.min(measuredH, desiredMax) && aboveSpace > belowSpace) {
      direction = 'up'
    }

    // Compute maxHeight to fit in viewport minus small padding
    const padding = 8
    const fitMax = direction === 'down'
      ? Math.max(100, Math.min(desiredMax, belowSpace - GAP - padding))
      : Math.max(100, Math.min(desiredMax, aboveSpace - GAP - padding))

    const topPos = direction === 'down'
      ? rect.bottom + window.scrollY + GAP
      : rect.top + window.scrollY - GAP - (menuRef.current?.offsetHeight || fitMax)

    setMenuDirection(direction)
    setMenuMaxHeight(fitMax)
    setCoords({
      top: topPos,
      left: rect.left + window.scrollX,
      width: rect.width
    })
  }

  // Resolve a z-index that stays above Kendo Dialog overlay/windows when rendered in a portal
  const resolveBaseZIndex = () => {
    try {
      // Look for common Kendo containers near the trigger
      const host = dropdownRef.current?.closest(
        '.k-dialog, .k-window, .k-animation-container, .k-dialog-wrapper'
      )
      if (host) {
        const z = window.getComputedStyle(host).zIndex
        const parsed = Number.parseInt(z, 10)
        if (!Number.isNaN(parsed)) return parsed
      }
      // Fallback: try overlay
      const overlay = document.querySelector('.k-overlay, .k-dialog-wrapper')
      if (overlay) {
        const z = window.getComputedStyle(overlay).zIndex
        const parsed = Number.parseInt(z, 10)
        if (!Number.isNaN(parsed)) return parsed
      }
    } catch (_) { }
    // Safe fallback higher than typical overlays
    return 10000
  }

  useEffect(() => {
    if (!isOpen) return
    setMenuPositioned(false)
    updatePosition()
    // Ensure we measure once the menu is painted and then mark as positioned
    const raf = requestAnimationFrame(() => {
      updatePosition()
      setMenuPositioned(true)
    })
    // compute z-index once opened
    const base = resolveBaseZIndex()
    setMenuZIndex(base + 2)
    const opts = { passive: true }
    window.addEventListener('scroll', updatePosition, opts)
    window.addEventListener('resize', updatePosition)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
      setMenuPositioned(false)
    }
  }, [isOpen])

  const handleOptionClick = (value, displayValue, index) => {
    setSelectedValue(value)
    setCurrentOptionIndex(index)
    setIsOpen(false)
    onDropDownChange({
      target: {
        value: value,
        id: optionsValue,
        selectedOptions: [{ value, textContent: displayValue }],
      },
    })
  }

  const handleToggleDropdown = () => {
    if (!disable && !alwaysOpen) {
      setIsOpen((prev) => !prev)
    }
  }

  const handleKeyPress = (event) => {
    const { key } = event
    const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ']

    if (!isOpen && openKeys.includes(key)) {
      event.preventDefault()
      setIsOpen(true)
    } else if (isOpen) {
      switch (key) {
        case 'Escape':
          event.preventDefault()
          if (!alwaysOpen) {
            setIsOpen(false)
          }
          break
        case 'ArrowDown':
          event.preventDefault()
          moveFocusDown()
          break
        case 'ArrowUp':
          event.preventDefault()
          moveFocusUp()
          break
        case 'Enter':
          event.preventDefault()
          selectCurrentOption()
          break
        default:
          break
      }
    }
  }

  const moveFocusDown = () => {
    setCurrentOptionIndex((prevIndex) =>
      prevIndex < sortedOptions.length - 1 ? prevIndex + 1 : 0
    )
  }

  const moveFocusUp = () => {
    setCurrentOptionIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : sortedOptions.length - 1
    )
  }

  const selectCurrentOption = () => {
    const currentOption = sortedOptions[currentOptionIndex]
    const value = valueItem ? currentOption[valueItem] : currentOption
    const displayValue = currentOption[nameOption] || currentOption
    handleOptionClick(value, displayValue, currentOptionIndex)
  }

  useEffect(() => {
    if (alwaysOpen) setIsOpen(true)
  }, [alwaysOpen])

  useEffect(() => {
    if (isOpen && menuPositioned && optionRefs.current[currentOptionIndex]) {
      optionRefs.current[currentOptionIndex].scrollIntoView({
        behavior: 'instant',
        block: 'nearest',
      })
    }
  }, [currentOptionIndex, isOpen, menuPositioned])

  useEffect(() => {
    setSelectedValue(optionsValue)
  }, [optionsValue])

  useEffect(() => {
    const index = sortedOptions.findIndex(option =>
      valueItem ? option[valueItem] === optionsValue : option === optionsValue
    )
    setCurrentOptionIndex(index >= 0 ? index : 0)
  }, [optionsValue, sortedOptions, valueItem])

  const menuRef = useRef(null)

  useEffect(() => {
    if (!alwaysOpen) {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target) &&
          (!menuRef.current || !menuRef.current.contains(event.target))
        ) {
          setIsOpen(false)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [alwaysOpen])

  const selectedOption =
    sortedOptions.find((option) =>
      valueItem ? option[valueItem] === selectedValue : option === selectedValue
    ) ||
    sortedOptions[0] ||
    {}

  return (
    <div
      className={
        flex === true
          ? 'dropDownBtnHLPure-containerFlex'
          : 'dropDownBtnHLPure-container'
      }
      id={id}
    >
      {iconFilter && (
        <div className='filter-icon-div'>
          <img src={FilterIcon} alt='' className='filter-icon-img' />
        </div>
      )}
      <div
        className={
          flex === true
            ? 'dropDownBtnHLPure-container-textFlex'
            : 'dropDownBtnHLPure-container-text'
        }
      >
        <div>
          <label className='dropDownBtnHLPure-label' htmlFor={optionsValue}>
            {labelDropDown}
            {required && ' *'}
          </label>
          {subLabel && <div className='dropDownBtnHLPure-sublabel-container'>
            <label className='dropDownBtnHLPure-sublabel'>{subLabel}</label>
            {tooltipShowSubLabel &&
              <TooltipHLPure
                positionTooltip={positionTooltipSubLabel}
                titleTooltip={titleTooltipSubLabel}
                descriptionTooltip={descriptionTooltipSubLabel}
                subContent={subContentSubLabel}
                restContent={restContentSubLabel}
                informationIcon={informationIconSubLabel}
              />
            }
          </div>
          }
        </div>
        {tooltipShow ? (
          <TooltipHLPure
            positionTooltip={positionTooltip}
            titleTooltip={titleTooltip}
            descriptionTooltip={descriptionTooltip}
            subContent={subContent}
            restContent={restContent}
            informationIcon={informationIcon}
          />
        ) : null}
      </div>
      <div className='dropDownBtnHLPure-select-wrapper' ref={dropdownRef}>
        <div
          ref={buttonRef}
          className={`dropdown-display ${isOpen ? 'dropdown-open' : ''} ${disable ? 'dropdown-disable' : ''
            }`}
          onClick={handleToggleDropdown}
          onKeyDown={handleKeyPress}
          role='combobox'
          aria-disabled={disable}
          aria-required={required}
          aria-controls='listbox'
          aria-haspopup='listbox'
          aria-expanded={isOpen}
          tabIndex={0}
        >
          {selectedOption[nameOption] ||
            (typeof selectedOption === 'string' ? selectedOption : '') ||
            (required ? 'Select an option' : '')}
        </div>
        {isOpen &&
          ReactDOM.createPortal(
            <ul
              ref={menuRef}
              role='listbox'
              className={`dropdown-container ${sortedOptions.length >= 8 ? 'dropdown-lg' : ''} ${id ? id : ''} ${menuDirection === 'up' ? 'dropdown-container-top' : ''}`}
              style={{
                top: coords.top,
                left: coords.left,
                width: coords.width,
                zIndex: menuZIndex,
                visibility: menuPositioned ? 'visible' : 'hidden',
                maxHeight: menuMaxHeight,
                overflowY: 'auto',
              }}
            >
              {sortedOptions.map((item, index) => {
                const value = valueItem ? item[valueItem] : item
                const displayValue = item[nameOption] || item
                return (
                  <li
                    ref={(el) => (optionRefs.current[index] = el)}
                    role='option'
                    aria-selected={index === currentOptionIndex}
                    key={index}
                    className={`dropdown-option ${index === currentOptionIndex ? 'option-selected' : ''
                      }`}
                    onClick={() => handleOptionClick(value, displayValue, index)}
                  >
                    {displayValue}
                  </li>
                )
              })}
            </ul>,
            document.body
          )}
      </div>
      {showError && (
        <LabelErrorPure label={errorText} />
      )}
    </div>
  )
}

/**
 * @author Alan Aguilar
 * @description A native dropdown component for smaller screens.
 * @param {Object} props The properties passed to the component.
 * @param {string} props.labelDropDown The label for the dropdown.
 * @param {Array} props.optionsDropDown The options for the dropdown.
 * @param {function} props.onDropDownChange The function to call when the dropdown value changes.
 * @param {(string|number)} props.optionsValue The value of the selected option.
 * @param {string} props.valueItem The key to use for the value in the options array.
 * @param {string} props.nameOption The key to use for the display name in the options array.
 * @param {boolean} [props.flex] Whether to use flexbox for layout.
 * @param {boolean} [props.tooltipShow] Whether to show the tooltip.
 * @param {'top'|'left'|'right'|'bottom'} [props.positionTooltip] The position of the tooltip.
 * @param {string} [props.titleTooltip] The title of the tooltip.
 * @param {string} [props.descriptionTooltip] The description of the tooltip.
 * @param {string} [props.subContent] Additional content for the tooltip.
 * @param {string} [props.restContent] Additional content for the tooltip.
 * @param {boolean} [props.informationIcon] Whether to show the information icon.
 * @param {boolean} [props.disable] Whether the dropdown is disabled.
 * @param {boolean} [props.required] Whether the dropdown is required.
  * @param {boolean} [props.iconFilter] Whether to show the filter icon or not, default is false.
   * @param {boolean} [props.keepPlaceholderTop=true] When a placeholder like "Sin asignar" exists, keep it as the first option and do not sort it.
   * @param {boolean} [props.hidePlaceholder=false] When true, removes placeholder options (e.g., "Sin asignar") from the list entirely.
   * @param {string}  [props.sortLocale] Optional BCP-47 locale (e.g., 'es', 'en'); defaults to the browser locale.
 * @returns {JSX.Element} The rendered component.
 * @example
 * ```
 * jsx
 * <DropDownBtnHLPure
 *  labelDropDown={t('labels.fondo')}
 *  optionsDropDown={[ name: 'Fondo 1', value: 'fondo1' }, { name: 'Fondo 2', value: 'fondo2' }]}
 *  optionsValue={fund}
 *  valueItem={'value'}
 *  nameOption={'name'}
 *  onDropDownChange={(e) =>
 *    setFund(e.target.value)
 *  }
 *  required
 * />
 * ```
 */
const DropDownBtnHLPureNative = (props) => {
  const {
    labelDropDown,
    optionsDropDown,
    onDropDownChange,
    optionsValue,
    valueItem,
    nameOption,
    flex,
    tooltipShow,
    positionTooltip,
    titleTooltip,
    descriptionTooltip,
    subContent,
    restContent,
    informationIcon,
    disable,
    required,
    iconFilter = false,
    id = '',
    keepPlaceholderTop = true,
    hidePlaceholder = false,
    sortLocale,
    subLabel = "",
    tooltipShowSubLabel,
    positionTooltipSubLabel,
    titleTooltipSubLabel,
    descriptionTooltipSubLabel,
    subContentSubLabel,
    restContentSubLabel,
    informationIconSubLabel,
  } = props

  const sortedOptions = useSortedOptions(
    optionsDropDown,
    valueItem,
    nameOption,
    keepPlaceholderTop,
    hidePlaceholder,
    sortLocale
  )

  return (
    <div
      className={`${iconFilter ? 'filter-container' : ''} ${flex === true
        ? 'dropDownBtnHLPure-containerFlex'
        : 'dropDownBtnHLPure-container'}`
      }
      id={id}
    >
      {iconFilter && (
        <div className='filter-icon-div'>
          <img src={FilterIcon} alt='' className='filter-icon-img' />
        </div>
      )}
      <div
        className={
          flex === true
            ? 'dropDownBtnHLPure-container-textFlex'
            : 'dropDownBtnHLPure-container-text'
        }
      >
        <div>
          <label className='dropDownBtnHLPure-label' htmlFor={optionsValue}>
            {labelDropDown}
            {required && ' *'}
          </label>
          {subLabel && <div className='dropDownBtnHLPure-sublabel-container'>
            <label className='dropDownBtnHLPure-sublabel'>{subLabel}</label>
            {tooltipShowSubLabel &&
              <TooltipHLPure
                positionTooltip={positionTooltipSubLabel}
                titleTooltip={titleTooltipSubLabel}
                descriptionTooltip={descriptionTooltipSubLabel}
                subContent={subContentSubLabel}
                restContent={restContentSubLabel}
                informationIcon={informationIconSubLabel}
              />
            }
          </div>}
        </div>
        {tooltipShow ? (
          <TooltipHLPure
            positionTooltip={positionTooltip}
            titleTooltip={titleTooltip}
            descriptionTooltip={descriptionTooltip}
            subContent={subContent}
            restContent={restContent}
            informationIcon={informationIcon}
          />
        ) : (
          ''
        )}
      </div>
      <div className='dropDownBtnHLPure-select-wrapper'>
        <select
          className='dropDownBtnHLPure-select'
          onChange={onDropDownChange}
          id={optionsValue}
          disabled={disable}
          required={required}
          value={optionsValue}
        >
          {sortedOptions.map((arrayItem, index) => {
            return (
              <option
                value={valueItem !== '' ? arrayItem[valueItem] : arrayItem}
                key={index}
              >
                {arrayItem[nameOption] || arrayItem}
              </option>
            )
          })}
        </select>
      </div>
    </div>
  )
}

/**
 * @author Marissa Vargas
 * @date 2025-04-22
 * @description A responsive dropdown component that dynamically switches between a custom dropdown for desktop devices and a native dropdown for smaller screens.
 * @param {Object} props The properties passed to the component.
 * @param {string} [props.labelDropDown] The label for the dropdown.
 * @param {{[key: string]: any}[]} props.optionsDropDown The options for the dropdown.
 * @param {function} props.onDropDownChange The function to call when the dropdown value changes.
 * @param {(string|number|any)} props.optionsValue The value of the selected option.
 * @param {string} props.valueItem The key to use for the value in the options array.
 * @param {string} props.nameOption The key to use for the display name in the options array.
 * @param {boolean} [props.flex] Whether to use flexbox for layout.
 * @param {boolean} [props.tooltipShow] Whether to show the tooltip.
 * @param {'top'|'left'|'right'|'bottom'} [props.positionTooltip] The position of the tooltip.
 * @param {string} [props.titleTooltip] The title of the tooltip.
 * @param {string} [props.descriptionTooltip] The description of the tooltip.
 * @param {string} [props.subContent] Additional content for the tooltip.
 * @param {string} [props.restContent] Additional content for the tooltip.
 * @param {boolean} [props.informationIcon] Whether to show the information icon.
 * @param {boolean} [props.disable] Whether the dropdown is disabled.
 * @param {boolean} [props.required] Whether the dropdown is required.
 * @param {'bottom'|'top'} [props.position] The position of the option list, default is to 'bottom'.
 * @param {boolean} [props.iconFilter] Whether to show the filter icon or not, default is false.
 * @returns {JSX.Element} The rendered responsive dropdown component.
 * @see DropDownBtnHLPure for desktop dropdown implementation.
 * @see DropDownBtnHLPureNative for native dropdown implementation.
 * @example
 * ```jsx
 * <DropdownPure
 *   labelDropDown='Select an option'
 *   optionsDropDown={[
 *     { name: 'Option 1', value: 'option1' },
 *     { name: 'Option 2', value: 'option2' },
 *   ]}
 *   optionsValue='option1'
 *   valueItem='value'
 *   nameOption='name'
 *   onDropDownChange={(e) => console.log(e.target.value)}
 *   position='top'
 *   required
 * />
 * ```
 */
const DropdownPure = (props) => {
  const matches = useMediaQuery('(min-width:820px)')

  return matches ? (
    <DropDownBtnHLPure {...props} />
  ) : (
    <DropDownBtnHLPureNative {...props} />
  )
}

export { DropdownPure as default, DropDownBtnHLPureNative }